import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import {
  HoldingsBaselineService,
  type BaselineLine,
  type HoldingsBaseline,
} from '../services/holdings-baseline-service';
import { AccountWithBalance } from '../types/api';
import { accountLabel } from '../utils/account-label';

interface HoldingsBaselineDialogProps {
  open: boolean;
  account: AccountWithBalance | null;
  onClose: () => void;
  /** Called after a save or graduation so the parent can refresh. */
  onChanged?: () => void;
}

const emptyLine = (currency: string): BaselineLine => ({
  symbol: '',
  quantity: '',
  price: '',
  currency,
});

/**
 * "Start from a snapshot" for an investment account.
 *
 * Instead of reconstructing years of trades, the user records what the
 * account holds on one date. If a baseline already exists the dialog shows it
 * and offers the one-way graduation into transaction tracking.
 */
export const HoldingsBaselineDialog: React.FC<HoldingsBaselineDialogProps> = ({
  open,
  account,
  onClose,
  onChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<HoldingsBaseline | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmGraduate, setConfirmGraduate] = useState(false);

  const [asOf, setAsOf] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<BaselineLine[]>([]);

  const currency = account?.currency || 'CAD';

  useEffect(() => {
    if (!open || !account) return;
    let cancelled = false;
    setError(null);
    setConfirmGraduate(false);
    setLoading(true);
    HoldingsBaselineService.get(account.id)
      .then((b) => {
        if (cancelled) return;
        setBaseline(b);
        if (b) {
          setEditing(false);
          setAsOf(b.as_of.slice(0, 10));
          setNotes(b.notes ?? '');
          setLines(b.lines.map((l) => ({ ...l })));
        } else {
          setEditing(true);
          setAsOf(new Date().toISOString().slice(0, 10));
          setNotes('');
          setLines([emptyLine(account.currency || 'CAD')]);
        }
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load snapshot');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, account]);

  const setLine = (i: number, patch: Partial<BaselineLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const canSave =
    !!asOf &&
    lines.length > 0 &&
    lines.every((l) => l.symbol.trim() && Number(l.quantity) > 0 && Number(l.price) >= 0);

  const save = async () => {
    if (!account) return;
    setSaving(true);
    setError(null);
    try {
      const stored = await HoldingsBaselineService.put(account.id, {
        as_of: asOf,
        notes: notes.trim() || undefined,
        lines: lines.map((l) => ({
          symbol: l.symbol.trim().toUpperCase(),
          quantity: l.quantity,
          price: l.price,
          currency: l.currency || currency,
        })),
      });
      setBaseline(stored);
      setEditing(false);
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || 'Failed to save snapshot');
    } finally {
      setSaving(false);
    }
  };

  const graduate = async () => {
    if (!account) return;
    setSaving(true);
    setError(null);
    try {
      await HoldingsBaselineService.graduate(account.id);
      setConfirmGraduate(false);
      onChanged?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to graduate account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {baseline ? 'Holdings snapshot' : 'Start from a snapshot'}
          {account && (
            <Typography variant="body2" color="text.secondary">
              {accountLabel(account)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : editing ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Record what this account holds on one date instead of entering its
                full trade history. Performance and holdings start from here; you
                can graduate to transaction tracking later.
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="As of"
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 200 }}
                />
                <TextField
                  label="Notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField
                          size="small" placeholder="XEQT.TO" value={l.symbol}
                          onChange={(e) => setLine(i, { symbol: e.target.value })}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small" placeholder="100" value={l.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value })}
                          sx={{ width: 110 }} inputProps={{ style: { textAlign: 'right' } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small" placeholder="31.25" value={l.price}
                          onChange={(e) => setLine(i, { price: e.target.value })}
                          sx={{ width: 110 }} inputProps={{ style: { textAlign: 'right' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" value={l.currency}
                          onChange={(e) => setLine(i, { currency: e.target.value.toUpperCase() })}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                          disabled={lines.length === 1}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box>
                <Button
                  startIcon={<Add />}
                  size="small"
                  onClick={() => setLines((prev) => [...prev, emptyLine(currency)])}
                >
                  Add position
                </Button>
              </Box>
            </Stack>
          ) : baseline ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This account is in snapshot mode: holdings start from the positions
                below as of <strong>{baseline.as_of.slice(0, 10)}</strong>
                {baseline.notes ? ` — ${baseline.notes}` : ''}. History before that
                date cannot be priced, so performance windows that reach earlier
                will say so honestly.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Currency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {baseline.lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.symbol}</TableCell>
                      <TableCell align="right">{l.quantity}</TableCell>
                      <TableCell align="right">{l.price}</TableCell>
                      <TableCell>{l.currency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          {!loading && baseline && !editing && (
            <>
              <Button onClick={() => setEditing(true)}>Update snapshot</Button>
              <Button color="warning" onClick={() => setConfirmGraduate(true)}>
                Graduate to transaction tracking
              </Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
          {editing && (
            <Button variant="contained" onClick={() => void save()} disabled={saving || !canSave}>
              {saving ? 'Saving…' : 'Save snapshot'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmGraduate} onClose={() => setConfirmGraduate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Graduate to transaction tracking?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography sx={{ mb: 1.5 }}>
              Each snapshot position becomes a <strong>TRANSFER_IN</strong> trade
            dated {baseline ? baseline.as_of.slice(0, 10) : 'the snapshot date'} at
              the snapshot price, and the account switches to normal transaction
              tracking. From then on you record buys, sells and dividends as they
              happen.
            </Typography>
            <Typography sx={{ mb: 1.5 }}>
              The snapshot price becomes the starting cost base, which is usually
              NOT your true adjusted cost base &mdash; review and correct the cost
              of each position afterwards on the Portfolio (ACB) page, especially
              if you will rely on it at tax time.
            </Typography>
            <Typography>This cannot be undone.</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGraduate(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => void graduate()} disabled={saving}>
            {saving ? 'Working…' : 'Graduate account'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
